import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Play, ExternalLink, Zap, Wrench, RotateCcw, Plus, Trash2, Newspaper, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expertApi } from '../services/expertApi';
import {
  useTradingConfig,
  useUpdateConfig,
  useUserTradingConfig,
  useUpdateUserTradingConfig,
  useTriggerReview,
  useRepairTradeLinks,
  useBackfillStrategyParameters,
  useEAMappings,
  useUsersForMapping,
  useCreateEAMapping,
  useDeleteEAMapping,
} from '../hooks/useTradingAnalysis';

// ─── Per-user config field definitions ──────────────────────────

interface FieldDef {
  key: string;
  label: string;
  type: 'number' | 'boolean';
  min?: number;
  max?: number;
  step?: number;
}

const USER_FIELDS: FieldDef[] = [
  { key: 'confidence_threshold', label: 'Confidence Threshold', type: 'number', min: 0, max: 100 },
  { key: 'max_risk_percent', label: 'Max Risk per Trade (%)', type: 'number', min: 0.1, max: 10, step: 0.1 },
  { key: 'min_rr_ratio', label: 'Min Reward:Risk Ratio', type: 'number', min: 0.1, max: 10, step: 0.1 },
  { key: 'cooldown_minutes', label: 'Cooldown Between Trades (min)', type: 'number', min: 0, max: 120 },
  { key: 'max_daily_loss_percent', label: 'Max Daily Loss (%)', type: 'number', min: 1, max: 20, step: 0.5 },
  { key: 'smart_trigger_min_pips', label: 'Smart Trigger Min Pips', type: 'number', min: 1, max: 500 },
  { key: 'smart_trigger_zone_proximity_pips', label: 'Smart Trigger Zone Proximity (pips)', type: 'number', min: 1, max: 200 },
  { key: 'auto_trade_enabled', label: 'Auto-Trade', type: 'boolean' },
];

const BANKER_MODEL_DEFAULTS: Record<string, string> = {
  openai: 'gpt-4o',
  anthropic: 'claude-sonnet-4-6',
  gemini: 'gemini-2.0-flash',
};

// ─── Component ──────────────────────────────────────────────────

const TradingSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.is_admin ?? false;

  // Per-user config
  const { data: userConfig, isLoading: userLoading } = useUserTradingConfig();
  const updateUserConfig = useUpdateUserTradingConfig();

  // Global config (admin)
  const { data: globalConfig, isLoading: globalLoading } = useTradingConfig();
  const updateGlobalConfig = useUpdateConfig();

  const triggerReview = useTriggerReview();
  const repairLinks = useRepairTradeLinks();
  const backfill = useBackfillStrategyParameters();

  // Chart provider preference (v30-1-6)
  const qc = useQueryClient();
  const { data: bankerPrefs } = useQuery({
    queryKey: ['bankerPreferences'],
    queryFn: () => expertApi.getBankerPreferences(),
    staleTime: 5 * 60 * 1000,
  });
  const updateBankerPrefsMutation = useMutation({
    mutationFn: (data: Parameters<typeof expertApi.updateBankerPreferences>[0]) =>
      expertApi.updateBankerPreferences(data),
    onSuccess: (updated) => {
      qc.setQueryData(['bankerPreferences'], updated);
    },
  });
  const handleChartProviderChange = (provider: 'internal' | 'tradingview') => {
    updateBankerPrefsMutation.mutate({ chart_provider: provider });
  };

  // EA Mappings
  const { data: eaMappings } = useEAMappings();
  const { data: usersList } = useUsersForMapping();
  const createMapping = useCreateEAMapping();
  const deleteMapping = useDeleteEAMapping();
  const [newEAName, setNewEAName] = useState('');
  const [newEAUserId, setNewEAUserId] = useState<number | ''>('');

  const MODEL_OPTIONS: Record<string, { value: string; label: string }[]> = {
    anthropic: [
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5' },
      { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' },
    ],
    copilot: [
      { value: 'gpt-5', label: 'GPT-5' },
      { value: 'claude-sonnet-4-5', label: 'Claude Sonnet 4.5 (via Copilot)' },
      { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 (via Copilot)' },
      { value: 'gpt-4.1', label: 'GPT-4.1' },
    ],
    ollama: [
      { value: 'qwen3:4b-thinking', label: 'Qwen3 4B Thinking (Reasoning)' },
      { value: 'qwen3-vl:4b', label: 'Qwen3-VL 4B (Vision)' },
      { value: 'glm-ocr', label: 'GLM-OCR (Number Extraction)' },
    ],
    openai: [
      { value: 'gpt-5-mini', label: 'GPT-5 Mini (Primary)' },
      { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
      { value: 'gpt-4.1', label: 'GPT-4.1' },
      { value: 'gpt-5.2', label: 'GPT-5.2' },
      { value: 'o4-mini', label: 'o4-mini (Reasoning)' },
    ],
    kimi: [
      { value: 'kimi-k2.5', label: 'Kimi K2.5' },
    ],
    zai: [
      { value: 'glm-4.5v', label: 'GLM-4.5V (Vision)' },
      { value: 'glm-4.6v', label: 'GLM-4.6V (Vision)' },
      { value: 'glm-5', label: 'GLM-5' },
      { value: 'glm-4.7', label: 'GLM-4.7' },
      { value: 'glm-ocr', label: 'GLM-OCR (Chart Reading)' },
      { value: 'glm-4.5-flash', label: 'GLM-4.5 Flash (Free)' },
      { value: 'glm-4.7-flash', label: 'GLM-4.7 Flash (Free)' },
    ],
    gemini: [
      { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (Fast)' },
      { value: 'gemini-3-pro-preview-preview', label: 'Gemini 3 Pro' },
      { value: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro' },
    ],
  };

  // ─── Per-user form state ────────────────────────────────────────

  const [userForm, setUserForm] = useState<Record<string, number | boolean>>({});
  const [userSaved, setUserSaved] = useState(false);

  useEffect(() => {
    if (userConfig) {
      const vals: Record<string, number | boolean> = {};
      const cfg = userConfig as unknown as Record<string, number | boolean>;
      for (const f of USER_FIELDS) {
        vals[f.key] = cfg[f.key];
      }
      setUserForm(vals);
    }
  }, [userConfig]);

  const handleUserSave = () => {
    // Build update payload — only send fields that differ from the resolved values
    // The API interprets absent=don't change, null=reset, value=override
    const payload: Record<string, number | boolean | null> = {};
    for (const f of USER_FIELDS) {
      const val = userForm[f.key];
      if (val !== undefined) {
        payload[f.key] = val as number | boolean;
      }
    }
    updateUserConfig.mutate(payload, {
      onSuccess: () => {
        setUserSaved(true);
        setTimeout(() => setUserSaved(false), 2000);
      },
    });
  };

  const handleResetField = (fieldKey: string) => {
    // Send explicit null to reset to global default
    updateUserConfig.mutate({ [fieldKey]: null }, {
      onSuccess: () => {
        // The query will refetch and update userForm
      },
    });
  };

  // ─── Global form state ──────────────────────────────────────────

  const [globalForm, setGlobalForm] = useState({
    trading_analyst_provider: 'anthropic',
    trading_analyst_model: '',
    trading_reviewer_provider: 'anthropic',
    trading_reviewer_model: 'claude-sonnet-4-20250514',
    trading_analyst_daily_review_time: '22:00',
    trading_analyst_weekly_review_day: 'saturday',
    trading_analyst_monthly_review_day: 1,
    ollama_base_url: 'http://localhost:11434',
    ollama_vision_model: 'qwen3-vl:4b',
    ollama_ocr_model: '',
    openai_use_flex: true,
    // EA chart limits per tier
    ea_max_charts_free: 1,
    ea_max_charts_pro: 2,
    ea_max_charts_premium: 5,
    // Banker monitoring
    banker_cycle_interval_minutes: 60,
    banker_leads_stop_hour: 20,
    ipo_scan_stop_hour: 20,
    banker_lead_cooldown_days: 7,
    // Banker business rules
    banker_convergence_bonus: 10,
    banker_sector_bonus: 5,
    banker_high_confidence_floor: 75,
    banker_risk_warn_pct: 3.0,
    banker_risk_alert_pct: 5.0,
    banker_risk_critical_pct: 10.0,
    banker_pnl_minor_milestone_pct: 5.0,
    banker_pnl_major_milestone_pct: 10.0,
    banker_atr_proximity_multiplier: 1.5,
    banker_universe_target: 60,
    banker_min_market_cap_usd: 100000000,
    app_domain: 'localhost',
    teams_monitor_webhook_url: '',
    discord_monitor_webhook_url: '',
    teams_support_webhook_url: '',
    discord_support_webhook_url: '',
    banker_provider: 'openai',
    banker_model: 'gpt-4o',
    banker_fallback_provider: 'gemini',
    banker_fallback_model: 'gemini-2.0-flash',
    // Web search
    web_search_provider: 'exa',
    web_search_fallback_provider: 'brave',
    web_search_api_key: '',
    exa_api_key: '',
    // Video transcript
    video_provider: 'hybrid',
    video_model: 'gpt-4o',
    whisper_model: 'base',
    frame_interval: 5,
    gemini_api_key: '',
    gemini_model: 'gemini-3-pro-preview',
    gemini_video_model: 'gemini-3-pro-preview',
    // Mobile Push (v13-1-4 / v13-3-1)
    expo_access_token: '',
    push_worker_concurrency: 200,
    // Subscription tier rate limits (admin-1-1)
    free_trial_days: 14,
    tier_free_ea_max_calls: 1,
    tier_free_ea_window: 3600,
    tier_free_dash_window: 86400,
    tier_pro_ea_max_calls: 3,
    tier_pro_ea_window: 900,
    tier_pro_dash_window: 0,
    tier_premium_ea_max_calls: 3,
    tier_premium_ea_window: 300,
    tier_premium_dash_window: 0,
    expert_chat_pro_daily_limit: 50,
    expert_chat_premium_daily_limit: 500,
    // Market data cache TTLs (admin-3-1)
    cache_ttl_quote: 900,
    cache_ttl_historical: 86400,
    cache_ttl_heatmap: 900,
    // Strategy-Driven Signals (v22)
    signals_version: 'v1',
  });
  const [globalSaved, setGlobalSaved] = useState(false);
  const [globalValidationError, setGlobalValidationError] = useState<string | null>(null);
  const [bankerRulesErrors, setBankerRulesErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (globalConfig) {
      setGlobalForm({
        trading_analyst_provider: globalConfig.trading_analyst_provider || 'anthropic',
        trading_analyst_model: globalConfig.trading_analyst_model,
        trading_reviewer_provider: globalConfig.trading_reviewer_provider || 'anthropic',
        trading_reviewer_model: globalConfig.trading_reviewer_model || 'claude-sonnet-4-20250514',
        trading_analyst_daily_review_time: globalConfig.trading_analyst_daily_review_time,
        trading_analyst_weekly_review_day: globalConfig.trading_analyst_weekly_review_day,
        trading_analyst_monthly_review_day: globalConfig.trading_analyst_monthly_review_day,
        ollama_base_url: globalConfig.ollama_base_url || 'http://localhost:11434',
        ollama_vision_model: globalConfig.ollama_vision_model || 'qwen3-vl:4b',
        ollama_ocr_model: globalConfig.ollama_ocr_model || '',
        openai_use_flex: globalConfig.openai_use_flex ?? true,
        // EA chart limits per tier
        ea_max_charts_free: globalConfig.ea_max_charts_free ?? 1,
        ea_max_charts_pro: globalConfig.ea_max_charts_pro ?? 2,
        ea_max_charts_premium: globalConfig.ea_max_charts_premium ?? 5,
        // Banker monitoring
        banker_cycle_interval_minutes: globalConfig.banker_cycle_interval_minutes ?? 60,
        banker_leads_stop_hour: globalConfig.banker_leads_stop_hour ?? 20,
        ipo_scan_stop_hour: globalConfig.ipo_scan_stop_hour ?? 20,
        banker_lead_cooldown_days: globalConfig.banker_lead_cooldown_days ?? 7,
        // Banker business rules
        banker_convergence_bonus: globalConfig.banker_convergence_bonus ?? 10,
        banker_sector_bonus: globalConfig.banker_sector_bonus ?? 5,
        banker_high_confidence_floor: globalConfig.banker_high_confidence_floor ?? 75,
        banker_risk_warn_pct: globalConfig.banker_risk_warn_pct ?? 3.0,
        banker_risk_alert_pct: globalConfig.banker_risk_alert_pct ?? 5.0,
        banker_risk_critical_pct: globalConfig.banker_risk_critical_pct ?? 10.0,
        banker_pnl_minor_milestone_pct: globalConfig.banker_pnl_minor_milestone_pct ?? 5.0,
        banker_pnl_major_milestone_pct: globalConfig.banker_pnl_major_milestone_pct ?? 10.0,
        banker_atr_proximity_multiplier: globalConfig.banker_atr_proximity_multiplier ?? 1.5,
        banker_universe_target: globalConfig.banker_universe_target ?? 60,
        banker_min_market_cap_usd: globalConfig.banker_min_market_cap_usd ?? 100000000,
        app_domain: globalConfig.app_domain || 'localhost',
        teams_monitor_webhook_url: globalConfig.teams_monitor_webhook_url || '',
        discord_monitor_webhook_url: globalConfig.discord_monitor_webhook_url || '',
        teams_support_webhook_url: globalConfig.teams_support_webhook_url || '',
        discord_support_webhook_url: globalConfig.discord_support_webhook_url || '',
        banker_provider: globalConfig.banker_provider || 'openai',
        banker_model: globalConfig.banker_model || 'gpt-4o',
        banker_fallback_provider: globalConfig.banker_fallback_provider || 'gemini',
        banker_fallback_model: globalConfig.banker_fallback_model || 'gemini-2.0-flash',
        // Web search
        web_search_provider: globalConfig.web_search_provider || 'exa',
        web_search_fallback_provider: globalConfig.web_search_fallback_provider || 'brave',
        web_search_api_key: globalConfig.web_search_api_key || '',
        exa_api_key: globalConfig.exa_api_key || '',
        // Video transcript
        video_provider: globalConfig.video_provider || 'hybrid',
        video_model: globalConfig.video_model || 'gpt-4o',
        whisper_model: globalConfig.whisper_model || 'base',
        frame_interval: globalConfig.frame_interval ?? 5,
        gemini_api_key: globalConfig.gemini_api_key || '',
        gemini_model: globalConfig.gemini_model || 'gemini-3-pro-preview',
        gemini_video_model: globalConfig.gemini_video_model || 'gemini-3-pro-preview',
        // Mobile Push (v13-1-4 / v13-3-1)
        expo_access_token: globalConfig.expo_access_token || '',
        push_worker_concurrency: globalConfig.push_worker_concurrency ?? 200,
        // Subscription tier rate limits (admin-1-1)
        free_trial_days: globalConfig.free_trial_days ?? 14,
        tier_free_ea_max_calls: globalConfig.tier_free_ea_max_calls ?? 1,
        tier_free_ea_window: globalConfig.tier_free_ea_window ?? 3600,
        tier_free_dash_window: globalConfig.tier_free_dash_window ?? 86400,
        tier_pro_ea_max_calls: globalConfig.tier_pro_ea_max_calls ?? 3,
        tier_pro_ea_window: globalConfig.tier_pro_ea_window ?? 900,
        tier_pro_dash_window: globalConfig.tier_pro_dash_window ?? 0,
        tier_premium_ea_max_calls: globalConfig.tier_premium_ea_max_calls ?? 3,
        tier_premium_ea_window: globalConfig.tier_premium_ea_window ?? 300,
        tier_premium_dash_window: globalConfig.tier_premium_dash_window ?? 0,
        expert_chat_pro_daily_limit: globalConfig.expert_chat_pro_daily_limit ?? 50,
        expert_chat_premium_daily_limit: globalConfig.expert_chat_premium_daily_limit ?? 500,
        // Market data cache TTLs (admin-3-1)
        cache_ttl_quote: globalConfig.cache_ttl_quote ?? 900,
        cache_ttl_historical: globalConfig.cache_ttl_historical ?? 86400,
        cache_ttl_heatmap: globalConfig.cache_ttl_heatmap ?? 900,
        // Strategy-Driven Signals (v22)
        signals_version: globalConfig.signals_version || 'v1',
      });
    }
  }, [globalConfig]);

  const handleGlobalSave = () => {
    // Validate cache TTL minimums
    if (
      globalForm.cache_ttl_quote < 60 ||
      globalForm.cache_ttl_historical < 60 ||
      globalForm.cache_ttl_heatmap < 60
    ) {
      setGlobalValidationError('Minimum cache duration is 60 seconds');
      return;
    }
    setGlobalValidationError(null);
    // Client-side validation for banker business rules
    const errors: Record<string, string> = {};
    if (globalForm.banker_convergence_bonus < 0 || globalForm.banker_convergence_bonus > 100) {
      errors.banker_convergence_bonus = 'Must be between 0 and 100';
    }
    if (globalForm.banker_sector_bonus < 0 || globalForm.banker_sector_bonus > 100) {
      errors.banker_sector_bonus = 'Must be between 0 and 100';
    }
    if (globalForm.banker_high_confidence_floor < 0 || globalForm.banker_high_confidence_floor > 100) {
      errors.banker_high_confidence_floor = 'Must be between 0 and 100';
    }
    if (globalForm.banker_risk_warn_pct <= 0) {
      errors.banker_risk_warn_pct = 'Must be greater than 0';
    }
    if (globalForm.banker_risk_alert_pct <= 0) {
      errors.banker_risk_alert_pct = 'Must be greater than 0';
    }
    if (globalForm.banker_risk_critical_pct <= 0) {
      errors.banker_risk_critical_pct = 'Must be greater than 0';
    }
    if (globalForm.banker_pnl_minor_milestone_pct <= 0) {
      errors.banker_pnl_minor_milestone_pct = 'Must be greater than 0';
    }
    if (globalForm.banker_pnl_major_milestone_pct <= 0) {
      errors.banker_pnl_major_milestone_pct = 'Must be greater than 0';
    }
    if (globalForm.banker_atr_proximity_multiplier <= 0) {
      errors.banker_atr_proximity_multiplier = 'Must be greater than 0';
    }
    if (globalForm.banker_universe_target < 1) {
      errors.banker_universe_target = 'Must be at least 1';
    }
    if (globalForm.banker_min_market_cap_usd < 0) {
      errors.banker_min_market_cap_usd = 'Must be 0 or greater';
    }
    setBankerRulesErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Strip API key fields if empty or sentinel — don't clear real keys
    const payload = { ...globalForm };
    if (!payload.gemini_api_key || payload.gemini_api_key === 'configured') {
      delete (payload as Record<string, unknown>).gemini_api_key;
    }
    if (!payload.web_search_api_key || payload.web_search_api_key === 'configured') {
      delete (payload as Record<string, unknown>).web_search_api_key;
    }
    if (!payload.exa_api_key || payload.exa_api_key === 'configured') {
      delete (payload as Record<string, unknown>).exa_api_key;
    }
    updateGlobalConfig.mutate(payload, {
      onSuccess: () => {
        setGlobalSaved(true);
        setTimeout(() => setGlobalSaved(false), 2000);
      },
    });
  };

  // ─── News filter form state (per-user) ────────────────────────

  const NEWS_FIELDS_KEYS = [
    'news_filter_mode', 'news_block_minutes_before', 'news_block_minutes_after',
    'news_cautious_confidence_penalty', 'news_impact_filter', 'news_country_filter',
    'news_pre_analysis_enabled', 'news_pre_analysis_window_minutes',
  ] as const;

  const [newsForm, setNewsForm] = useState<Record<string, string | number | boolean>>({
    news_filter_mode: 'cautious',
    news_block_minutes_before: 30,
    news_block_minutes_after: 15,
    news_cautious_confidence_penalty: 15,
    news_impact_filter: 'High,Medium,Low',
    news_country_filter: '',
    news_pre_analysis_enabled: true,
    news_pre_analysis_window_minutes: 120,
  });
  const [newsSaved, setNewsSaved] = useState(false);

  useEffect(() => {
    if (userConfig) {
      const cfg = userConfig as unknown as Record<string, string | number | boolean>;
      const vals: Record<string, string | number | boolean> = {};
      for (const key of NEWS_FIELDS_KEYS) {
        if (cfg[key] !== undefined) vals[key] = cfg[key];
      }
      if (Object.keys(vals).length > 0) setNewsForm(prev => ({ ...prev, ...vals }));
    }
  }, [userConfig]);

  const handleNewsSave = () => {
    const payload: Record<string, string | number | boolean | null> = {};
    for (const key of NEWS_FIELDS_KEYS) {
      const val = newsForm[key];
      if (val !== undefined) payload[key] = val;
    }
    updateUserConfig.mutate(payload, {
      onSuccess: () => {
        setNewsSaved(true);
        setTimeout(() => setNewsSaved(false), 2000);
      },
    });
  };

  const handleNewsResetField = (fieldKey: string) => {
    updateUserConfig.mutate({ [fieldKey]: null }, {
      onSuccess: () => {},
    });
  };

  // ─── Tools ──────────────────────────────────────────────────────

  const [reviewStatus, setReviewStatus] = useState<string | null>(null);
  const [repairStatus, setRepairStatus] = useState<string | null>(null);
  const [backfillStatus, setBackfillStatus] = useState<string | null>(null);

  const handleReview = (period: 'daily' | 'weekly' | 'monthly') => {
    setReviewStatus(`Running ${period} review...`);
    triggerReview.mutate(period, {
      onSuccess: () => setReviewStatus(`${period} review completed`),
      onError: (e) => setReviewStatus(`Review failed: ${e.message}`),
    });
  };

  const inputClass = 'bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white w-full focus:outline-none focus:border-teal-500';

  if (userLoading || globalLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="w-6 h-6 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/trading')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Trading Settings</h1>
          <p className="text-slate-400 text-sm">Configure your trading parameters</p>
        </div>
      </div>

      {/* ══════ Section 1: Your Trading Parameters ══════ */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Your Trading Parameters</h2>
          <button
            onClick={handleUserSave}
            disabled={updateUserConfig.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            <Save size={16} />
            {userSaved ? 'Saved!' : 'Save My Settings'}
          </button>
        </div>

        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {USER_FIELDS.filter(f => f.type === 'number').map((f) => {
              const isOverridden = userConfig?.overrides?.[f.key] ?? false;
              const globalDefault = userConfig?.global_defaults?.[f.key];
              return (
                <div key={f.key}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-slate-400">{f.label}</label>
                    {isOverridden && (
                      <button
                        onClick={() => handleResetField(f.key)}
                        className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors"
                      >
                        <RotateCcw size={10} />
                        Reset to default
                      </button>
                    )}
                  </div>
                  <input
                    type="number"
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={userForm[f.key] as number ?? ''}
                    onChange={(e) => setUserForm({ ...userForm, [f.key]: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {!isOverridden && globalDefault !== undefined && (
                    <p className="text-xs text-slate-500 mt-0.5">(default: {String(globalDefault)})</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Auto-Trade Toggle */}
          {(() => {
            const f = USER_FIELDS.find(f => f.key === 'auto_trade_enabled')!;
            const isOverridden = userConfig?.overrides?.[f.key] ?? false;
            const globalDefault = userConfig?.global_defaults?.[f.key];
            const enabled = userForm[f.key] as boolean ?? false;
            return (
              <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-300">{f.label}</h3>
                    {isOverridden && (
                      <button
                        onClick={() => handleResetField(f.key)}
                        className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors"
                      >
                        <RotateCcw size={10} />
                        Reset
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    When enabled, signals above the confidence threshold will be forwarded to EA for execution.
                    {!isOverridden && globalDefault !== undefined && (
                      <span> (default: {globalDefault ? 'on' : 'off'})</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (enabled || window.confirm('Enable auto-trade? High-confidence signals will be sent directly to the EA for execution.')) {
                      setUserForm({ ...userForm, [f.key]: !enabled });
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    enabled ? 'bg-teal-500' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ══════ Section: Chart Provider ══════ */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">Chart Provider</h2>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-3">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Chart Provider</h3>
            <div className="flex gap-3">
              {(['internal', 'tradingview'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => handleChartProviderChange(p)}
                  disabled={updateBankerPrefsMutation.isPending}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                    (bankerPrefs?.chart_provider ?? 'internal') === p
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {p === 'internal' ? 'Internal Charts' : 'TradingView Charts'}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              TradingView Charts include the full indicator suite and real-time data with volume.
            </p>
          </div>
        </div>
      </div>

      {/* ══════ Section 2: EA Mappings ══════ */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">EA Mappings</h2>
        <p className="text-xs text-slate-500 -mt-2">
          Map each EA instance to a user account. The EA will use that user's trading parameters (confidence threshold, daily loss limit, etc.).
        </p>

        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
          {/* Existing mappings */}
          {eaMappings && eaMappings.length > 0 ? (
            <div className="space-y-2">
              {eaMappings.map((m) => (
                <div
                  key={m.ea_name}
                  className="flex items-center justify-between bg-slate-700/50 rounded-lg px-4 py-2.5"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-white">{m.ea_name}</span>
                    <span className="text-xs text-slate-400">→</span>
                    <span className="text-sm text-slate-300">{m.user_email || `User #${m.user_id}`}</span>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm(`Remove mapping for "${m.ea_name}"?`)) {
                        deleteMapping.mutate(m.ea_name);
                      }
                    }}
                    disabled={deleteMapping.isPending}
                    className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No EA mappings configured. EAs will use global defaults.</p>
          )}

          {/* Add new mapping */}
          <div className="flex items-end gap-3 pt-2 border-t border-slate-700/50">
            <div className="flex-1">
              <label className="text-xs text-slate-400 block mb-1">EA Name</label>
              <input
                type="text"
                value={newEAName}
                onChange={(e) => setNewEAName(e.target.value)}
                placeholder="e.g. LLM_Analyst"
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-slate-400 block mb-1">User</label>
              {isAdmin ? (
                <select
                  value={newEAUserId}
                  onChange={(e) => setNewEAUserId(e.target.value ? Number(e.target.value) : '')}
                  className={inputClass}
                >
                  <option value="">Select user...</option>
                  {usersList?.map((u) => (
                    <option key={u.id} value={u.id}>{u.email}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={user?.email ?? ''}
                  readOnly
                  className={`${inputClass} opacity-60 cursor-not-allowed`}
                />
              )}
            </div>
            <button
              onClick={() => {
                const targetUserId = isAdmin ? newEAUserId as number : user?.id;
                if (newEAName.trim() && targetUserId) {
                  createMapping.mutate(
                    { ea_name: newEAName.trim(), user_id: targetUserId },
                    {
                      onSuccess: () => {
                        setNewEAName('');
                        setNewEAUserId('');
                      },
                    }
                  );
                }
              }}
              disabled={!newEAName.trim() || (isAdmin ? !newEAUserId : !user?.id) || createMapping.isPending}
              className="flex items-center gap-1.5 px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-semibold rounded-lg transition-colors text-sm whitespace-nowrap"
            >
              <Plus size={14} />
              Add
            </button>
          </div>
          {createMapping.isError && (
            <p className="text-red-400 text-xs">{createMapping.error?.message || 'Failed to create mapping'}</p>
          )}
        </div>
      </div>

      {/* ══════ Section 3: News Filter (per-user) ══════ */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Newspaper size={18} className="text-amber-400" />
            <h2 className="text-lg font-semibold text-white">News Filter</h2>
          </div>
          <button
            onClick={handleNewsSave}
            disabled={updateUserConfig.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            <Save size={16} />
            {newsSaved ? 'Saved!' : 'Save News Settings'}
          </button>
        </div>

        {/* Mode indicator */}
        <div className={`rounded-lg px-4 py-3 text-sm ${
          newsForm.news_filter_mode === 'block'
            ? 'bg-red-500/10 border border-red-500/30 text-red-300'
            : newsForm.news_filter_mode === 'cautious'
            ? 'bg-amber-500/10 border border-amber-500/30 text-amber-300'
            : newsForm.news_filter_mode === 'allow'
            ? 'bg-teal-500/10 border border-teal-500/30 text-teal-300'
            : 'bg-slate-700/50 border border-slate-600 text-slate-300'
        }`}>
          {newsForm.news_filter_mode === 'block' && 'Block mode: Trading is paused before/after economic events matching your impact filter.'}
          {newsForm.news_filter_mode === 'cautious' && 'Cautious mode: Confidence is reduced near economic events matching your impact filter.'}
          {newsForm.news_filter_mode === 'allow' && 'Allow mode: LLM provides directional analysis before upcoming events. All impact levels visible.'}
        </div>

        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
          {/* Filter Mode */}
          {(() => {
            const key = 'news_filter_mode';
            const isOverridden = userConfig?.overrides?.[key] ?? false;
            const globalDefault = userConfig?.global_defaults?.[key];
            return (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-400">Filter Mode</label>
                  {isOverridden && (
                    <button onClick={() => handleNewsResetField(key)} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                      <RotateCcw size={10} /> Reset to default
                    </button>
                  )}
                </div>
                <select
                  value={newsForm.news_filter_mode as string}
                  onChange={(e) => setNewsForm({ ...newsForm, news_filter_mode: e.target.value })}
                  className={inputClass}
                >
                  <option value="allow">Allow (pre-news analysis)</option>
                  <option value="cautious">Cautious (reduce confidence)</option>
                  <option value="block">Block (pause trading)</option>
                </select>
                {!isOverridden && globalDefault !== undefined && (
                  <p className="text-xs text-slate-500 mt-0.5">(default: {String(globalDefault)})</p>
                )}
              </div>
            );
          })()}

          {/* Block mode settings */}
          {newsForm.news_filter_mode === 'block' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'news_block_minutes_before', label: 'Block Minutes Before Event', min: 5, max: 120 },
                { key: 'news_block_minutes_after', label: 'Block Minutes After Event', min: 5, max: 60 },
              ].map(({ key, label, min, max }) => {
                const isOverridden = userConfig?.overrides?.[key] ?? false;
                const globalDefault = userConfig?.global_defaults?.[key];
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-slate-400">{label}</label>
                      {isOverridden && (
                        <button onClick={() => handleNewsResetField(key)} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                          <RotateCcw size={10} /> Reset
                        </button>
                      )}
                    </div>
                    <input
                      type="number" min={min} max={max}
                      value={newsForm[key] as number ?? ''}
                      onChange={(e) => setNewsForm({ ...newsForm, [key]: Number(e.target.value) })}
                      className={inputClass}
                    />
                    {!isOverridden && globalDefault !== undefined && (
                      <p className="text-xs text-slate-500 mt-0.5">(default: {String(globalDefault)})</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Cautious mode settings */}
          {newsForm.news_filter_mode === 'cautious' && (() => {
            const key = 'news_cautious_confidence_penalty';
            const isOverridden = userConfig?.overrides?.[key] ?? false;
            const globalDefault = userConfig?.global_defaults?.[key];
            return (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-400">Confidence Penalty</label>
                  {isOverridden && (
                    <button onClick={() => handleNewsResetField(key)} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                      <RotateCcw size={10} /> Reset
                    </button>
                  )}
                </div>
                <input
                  type="number" min={1} max={50}
                  value={newsForm[key] as number ?? ''}
                  onChange={(e) => setNewsForm({ ...newsForm, [key]: Number(e.target.value) })}
                  className={inputClass}
                />
                <p className="text-xs text-slate-500 mt-0.5">
                  Points subtracted from confidence when events are upcoming
                  {!isOverridden && globalDefault !== undefined && ` (default: ${globalDefault})`}
                </p>
              </div>
            );
          })()}

          {/* Allow mode: pre-analysis settings */}
          {newsForm.news_filter_mode === 'allow' && (
            <div className="space-y-4">
              {/* Pre-analysis enabled toggle */}
              {(() => {
                const key = 'news_pre_analysis_enabled';
                const isOverridden = userConfig?.overrides?.[key] ?? false;
                const globalDefault = userConfig?.global_defaults?.[key];
                const enabled = newsForm[key] as boolean ?? true;
                return (
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-slate-300">Pre-News Directional Analysis</label>
                        {isOverridden && (
                          <button onClick={() => handleNewsResetField(key)} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                            <RotateCcw size={10} /> Reset
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        LLM analyzes upcoming events and provides BUY/SELL/HOLD bias
                        {!isOverridden && globalDefault !== undefined && ` (default: ${globalDefault ? 'on' : 'off'})`}
                      </p>
                    </div>
                    <button
                      onClick={() => setNewsForm({ ...newsForm, [key]: !enabled })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        enabled ? 'bg-teal-500' : 'bg-slate-600'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        enabled ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                );
              })()}

              {/* Pre-analysis window minutes */}
              {newsForm.news_pre_analysis_enabled && (() => {
                const key = 'news_pre_analysis_window_minutes';
                const isOverridden = userConfig?.overrides?.[key] ?? false;
                const globalDefault = userConfig?.global_defaults?.[key];
                return (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs text-slate-400">Analysis Window (minutes before event)</label>
                      {isOverridden && (
                        <button onClick={() => handleNewsResetField(key)} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                          <RotateCcw size={10} /> Reset
                        </button>
                      )}
                    </div>
                    <input
                      type="number" min={15} max={240} step={15}
                      value={newsForm[key] as number ?? 120}
                      onChange={(e) => setNewsForm({ ...newsForm, [key]: Number(e.target.value) })}
                      className={inputClass}
                    />
                    <p className="text-xs text-slate-500 mt-0.5">
                      How early to request directional analysis before a high-impact event
                      {!isOverridden && globalDefault !== undefined && ` (default: ${globalDefault})`}
                    </p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Impact filter */}
          {(() => {
            const key = 'news_impact_filter';
            const isOverridden = userConfig?.overrides?.[key] ?? false;
            const globalDefault = userConfig?.global_defaults?.[key];
            return (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-400">Impact Filter</label>
                  {isOverridden && (
                    <button onClick={() => handleNewsResetField(key)} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                      <RotateCcw size={10} /> Reset
                    </button>
                  )}
                </div>
                <select
                  value={newsForm.news_impact_filter as string}
                  onChange={(e) => setNewsForm({ ...newsForm, news_impact_filter: e.target.value })}
                  className={inputClass}
                >
                  <option value="High">High</option>
                  <option value="High,Medium">High + Medium</option>
                  <option value="High,Medium,Low">All</option>
                </select>
                {!isOverridden && globalDefault !== undefined && (
                  <p className="text-xs text-slate-500 mt-0.5">(default: {String(globalDefault)})</p>
                )}
              </div>
            );
          })()}

          {/* Country filter */}
          {(() => {
            const key = 'news_country_filter';
            const isOverridden = userConfig?.overrides?.[key] ?? false;
            const globalDefault = userConfig?.global_defaults?.[key];
            return (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-400">Country Filter (optional)</label>
                  {isOverridden && (
                    <button onClick={() => handleNewsResetField(key)} className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors">
                      <RotateCcw size={10} /> Reset
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={newsForm.news_country_filter as string}
                  onChange={(e) => setNewsForm({ ...newsForm, news_country_filter: e.target.value })}
                  placeholder="Auto from symbol (e.g., USD,EUR)"
                  className={inputClass}
                />
                <p className="text-xs text-slate-500 mt-0.5">
                  Leave empty to auto-detect from trading symbol
                  {!isOverridden && globalDefault !== undefined && globalDefault !== '' && ` (default: ${String(globalDefault)})`}
                </p>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ══════ Section 4: Global Settings (admin only) ══════ */}
      {isAdmin && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Global Settings</h2>
            <button
              onClick={handleGlobalSave}
              disabled={updateGlobalConfig.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              <Save size={16} />
              {globalSaved ? 'Saved!' : 'Save Global Settings'}
            </button>
          </div>
          {globalValidationError && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-2 text-sm text-red-400">
              {globalValidationError}
            </div>
          )}

          {/* Analysis Provider & Model */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300">Analysis</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Provider</label>
                <select
                  value={globalForm.trading_analyst_provider}
                  onChange={(e) => {
                    const newProvider = e.target.value;
                    const models = MODEL_OPTIONS[newProvider] || MODEL_OPTIONS.anthropic;
                    setGlobalForm({
                      ...globalForm,
                      trading_analyst_provider: newProvider,
                      trading_analyst_model: models[0].value,
                    });
                  }}
                  className={inputClass}
                >
                  <option value="anthropic">Anthropic (Direct)</option>
                  <option value="copilot" disabled={!globalConfig?.copilot_available}>
                    GitHub Copilot{!globalConfig?.copilot_available ? ' (SDK not installed)' : ''}
                  </option>
                  <option value="ollama">Ollama (Local)</option>
                  <option value="openai">OpenAI</option>
                  <option value="gemini">Google Gemini</option>
                  <option value="kimi">Moonshot (Kimi)</option>
                  <option value="zai">Z.AI (Zhipu GLM)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Model</label>
                <select
                  value={globalForm.trading_analyst_model}
                  onChange={(e) => setGlobalForm({ ...globalForm, trading_analyst_model: e.target.value })}
                  className={inputClass}
                >
                  {(MODEL_OPTIONS[globalForm.trading_analyst_provider] || MODEL_OPTIONS.anthropic).map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Reviewer Provider & Model */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300">Reviewer (separate LLM for trade reviews)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Provider</label>
                <select
                  value={globalForm.trading_reviewer_provider}
                  onChange={(e) => {
                    const newProvider = e.target.value;
                    const models = MODEL_OPTIONS[newProvider] || MODEL_OPTIONS.anthropic;
                    setGlobalForm({
                      ...globalForm,
                      trading_reviewer_provider: newProvider,
                      trading_reviewer_model: models[0].value,
                    });
                  }}
                  className={inputClass}
                >
                  <option value="anthropic">Anthropic (Direct)</option>
                  <option value="copilot" disabled={!globalConfig?.copilot_available}>
                    GitHub Copilot{!globalConfig?.copilot_available ? ' (SDK not installed)' : ''}
                  </option>
                  <option value="ollama">Ollama (Local)</option>
                  <option value="openai">OpenAI</option>
                  <option value="gemini">Google Gemini</option>
                  <option value="kimi">Moonshot (Kimi)</option>
                  <option value="zai">Z.AI (Zhipu GLM)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Model</label>
                <select
                  value={globalForm.trading_reviewer_model}
                  onChange={(e) => setGlobalForm({ ...globalForm, trading_reviewer_model: e.target.value })}
                  className={inputClass}
                >
                  {(MODEL_OPTIONS[globalForm.trading_reviewer_provider] || MODEL_OPTIONS.anthropic).map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* OpenAI Flex Config (visible when any provider is openai) */}
          {(globalForm.trading_analyst_provider === 'openai' || globalForm.trading_reviewer_provider === 'openai') && (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-300">OpenAI Settings</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Flex processing batches requests for up to 50% cost savings. Responses may take up to 24 hours but typically complete within minutes.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm text-slate-300">Flex Processing (50% discount)</label>
                  <p className="text-xs text-slate-500 mt-0.5">Enable async batch processing for reduced cost</p>
                </div>
                <button
                  onClick={() => setGlobalForm({ ...globalForm, openai_use_flex: !globalForm.openai_use_flex })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    globalForm.openai_use_flex ? 'bg-teal-500' : 'bg-slate-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      globalForm.openai_use_flex ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Ollama Pipeline Config (visible when any provider is ollama) */}
          {(globalForm.trading_analyst_provider === 'ollama' || globalForm.trading_reviewer_provider === 'ollama') && (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-300">Ollama Pipeline</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Configure the local model pipeline. Vision model analyzes chart patterns, OCR model extracts precise numbers. Both run in parallel per timeframe.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-400 block mb-1">Ollama Base URL</label>
                  <input
                    type="text"
                    value={globalForm.ollama_base_url}
                    onChange={(e) => setGlobalForm({ ...globalForm, ollama_base_url: e.target.value })}
                    placeholder="http://localhost:11434"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Vision Model (pattern analysis)</label>
                  <select
                    value={globalForm.ollama_vision_model}
                    onChange={(e) => setGlobalForm({ ...globalForm, ollama_vision_model: e.target.value })}
                    className={inputClass}
                  >
                    {(MODEL_OPTIONS.ollama).filter(m => m.value !== 'glm-ocr').map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-0.5">Used when primary model is text-only (e.g., qwen3:4b-thinking)</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">OCR Model (number extraction)</label>
                  <select
                    value={globalForm.ollama_ocr_model}
                    onChange={(e) => setGlobalForm({ ...globalForm, ollama_ocr_model: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Disabled</option>
                    <option value="glm-ocr">GLM-OCR</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-0.5">Extracts price levels, timestamps, indicator values from charts</p>
                </div>
              </div>
              {globalForm.ollama_ocr_model && (
                <div className="bg-slate-700/30 rounded-lg px-3 py-2">
                  <p className="text-xs text-teal-400">
                    Dual pipeline active: <span className="text-white">{globalForm.ollama_ocr_model}</span> (OCR)
                    {' + '}
                    <span className="text-white">{globalForm.ollama_vision_model}</span> (Vision)
                    {' \u2192 '}
                    <span className="text-white">{globalForm.trading_analyst_model}</span> (Reasoning)
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Review Schedule */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300">Review Schedule</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Daily Review Time (UTC)</label>
                <input
                  type="time"
                  value={globalForm.trading_analyst_daily_review_time}
                  onChange={(e) => setGlobalForm({ ...globalForm, trading_analyst_daily_review_time: e.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Weekly Review Day</label>
                <select
                  value={globalForm.trading_analyst_weekly_review_day}
                  onChange={(e) => setGlobalForm({ ...globalForm, trading_analyst_weekly_review_day: e.target.value })}
                  className={inputClass}
                >
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((d) => (
                    <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Monthly Review Day</label>
                <input
                  type="number"
                  min={1}
                  max={28}
                  value={globalForm.trading_analyst_monthly_review_day}
                  onChange={(e) => setGlobalForm({ ...globalForm, trading_analyst_monthly_review_day: Number(e.target.value) })}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* EA Chart Mapping Limits */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300">EA Chart Mapping Limits</h3>
            <p className="text-xs text-slate-500">Maximum number of EA chart mappings allowed per subscription tier. Changes take effect immediately.</p>
            <div className="grid grid-cols-3 gap-4 max-w-sm">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Free</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={globalForm.ea_max_charts_free}
                  onChange={(e) => setGlobalForm({ ...globalForm, ea_max_charts_free: Number(e.target.value) })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Pro</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={globalForm.ea_max_charts_pro}
                  onChange={(e) => setGlobalForm({ ...globalForm, ea_max_charts_pro: Number(e.target.value) })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Premium</label>
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={globalForm.ea_max_charts_premium}
                  onChange={(e) => setGlobalForm({ ...globalForm, ea_max_charts_premium: Number(e.target.value) })}
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Subscription Tier Limits */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300">Subscription Tier Limits</h3>
            <p className="text-xs text-slate-500">
              Rate windows and daily quotas per subscription tier. Changes take effect immediately. Set window to 0 for unlimited.
            </p>

            {/* Free Trial Period */}
            <div className="max-w-xs">
              <label className="text-xs text-slate-400 block mb-1">Free Trial Period (days)</label>
              <input
                type="number"
                min={1}
                max={365}
                value={globalForm.free_trial_days}
                onChange={(e) => setGlobalForm({ ...globalForm, free_trial_days: Math.max(1, Number(e.target.value)) })}
                className={inputClass}
              />
              <p className="text-xs text-slate-500 mt-0.5">Days of AI access for free-tier users after signup. Min 1.</p>
            </div>

            {/* EA Calls per Window grid */}
            <div>
              <p className="text-xs text-slate-400 font-medium mb-2">EA Calls per Window</p>
              <div className="grid grid-cols-3 gap-4 max-w-sm">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Free</label>
                  <input
                    type="number"
                    min={1}
                    value={globalForm.tier_free_ea_max_calls}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_free_ea_max_calls: Math.max(1, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Pro</label>
                  <input
                    type="number"
                    min={1}
                    value={globalForm.tier_pro_ea_max_calls}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_pro_ea_max_calls: Math.max(1, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Premium</label>
                  <input
                    type="number"
                    min={1}
                    value={globalForm.tier_premium_ea_max_calls}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_premium_ea_max_calls: Math.max(1, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* EA Window (sec) grid */}
            <div>
              <p className="text-xs text-slate-400 font-medium mb-2">EA Window (sec) <span className="text-slate-500 font-normal">— 0 = unlimited</span></p>
              <div className="grid grid-cols-3 gap-4 max-w-sm">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Free</label>
                  <input
                    type="number"
                    min={0}
                    value={globalForm.tier_free_ea_window}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_free_ea_window: Math.max(0, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Pro</label>
                  <input
                    type="number"
                    min={0}
                    value={globalForm.tier_pro_ea_window}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_pro_ea_window: Math.max(0, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Premium</label>
                  <input
                    type="number"
                    min={0}
                    value={globalForm.tier_premium_ea_window}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_premium_ea_window: Math.max(0, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            {/* Dash Window (sec) grid */}
            <div>
              <p className="text-xs text-slate-400 font-medium mb-2">Dash Window (sec) <span className="text-slate-500 font-normal">— 0 = unlimited</span></p>
              <div className="grid grid-cols-3 gap-4 max-w-sm">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Free</label>
                  <input
                    type="number"
                    min={0}
                    value={globalForm.tier_free_dash_window}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_free_dash_window: Math.max(0, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Pro</label>
                  <input
                    type="number"
                    min={0}
                    value={globalForm.tier_pro_dash_window}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_pro_dash_window: Math.max(0, Number(e.target.value)) })}
                    className={inputClass}
                  />
                  <p className="text-xs text-slate-500 mt-0.5">0 = unlimited</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Premium</label>
                  <input
                    type="number"
                    min={0}
                    value={globalForm.tier_premium_dash_window}
                    onChange={(e) => setGlobalForm({ ...globalForm, tier_premium_dash_window: Math.max(0, Number(e.target.value)) })}
                    className={inputClass}
                  />
                  <p className="text-xs text-slate-500 mt-0.5">0 = unlimited</p>
                </div>
              </div>
            </div>

            {/* Expert Chat Daily Limits */}
            <div>
              <p className="text-xs text-slate-400 font-medium mb-2">Expert Chat Daily Limit</p>
              <div className="grid grid-cols-2 gap-4 max-w-xs">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Pro</label>
                  <input
                    type="number"
                    min={1}
                    value={globalForm.expert_chat_pro_daily_limit}
                    onChange={(e) => setGlobalForm({ ...globalForm, expert_chat_pro_daily_limit: Math.max(1, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Premium</label>
                  <input
                    type="number"
                    min={1}
                    value={globalForm.expert_chat_premium_daily_limit}
                    onChange={(e) => setGlobalForm({ ...globalForm, expert_chat_premium_daily_limit: Math.max(1, Number(e.target.value)) })}
                    className={inputClass}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">Maximum messages per user per day. Min 1.</p>
            </div>
          </div>

          {/* Strategy-Driven Signals */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300">Strategy-Driven Signals</h3>
            <div className="max-w-xs">
              <label className="text-xs text-slate-400 block mb-1">Signals Version</label>
              <select
                value={globalForm.signals_version}
                onChange={(e) => setGlobalForm({ ...globalForm, signals_version: e.target.value })}
                className={inputClass}
              >
                <option value="v1">v1 — Indicator-based (default)</option>
                <option value="v2">v2 — Strategy-driven (Pro/Premium)</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                v1: existing RSI/MACD thresholds for all users. v2: strategy-driven signals for Pro/Premium users with the strategy-driven-signals permission; Free users see v1 regardless.
              </p>
            </div>
          </div>

          {/* Banker Monitoring Settings */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-300">Banker Monitoring</h3>
            <div className="max-w-xs">
              <label className="text-xs text-slate-400 block mb-1">Cycle Interval (minutes)</label>
              <input
                type="number"
                min={15}
                max={480}
                step={15}
                value={globalForm.banker_cycle_interval_minutes}
                onChange={(e) => setGlobalForm({ ...globalForm, banker_cycle_interval_minutes: Number(e.target.value) })}
                className={inputClass}
              />
              <p className="text-xs text-slate-500 mt-0.5">
                How often the banker monitoring cycle runs. Min 15 min. Takes effect on next backend restart. Current default: 60 min.
              </p>
            </div>
            <div className="max-w-xs">
              <label className="text-xs text-slate-400 block mb-1">Lead Cooldown (days)</label>
              <input
                type="number"
                min={1}
                max={30}
                step={1}
                value={globalForm.banker_lead_cooldown_days}
                onChange={(e) => setGlobalForm({ ...globalForm, banker_lead_cooldown_days: Number(e.target.value) })}
                className={inputClass}
              />
              <p className="text-xs text-slate-500 mt-0.5">
                Minimum days before the same symbol can be re-sent as a lead. Bypassed if new confidence is 15+ points higher. Default: 7 days.
              </p>
            </div>
            <div className="border-t border-slate-700/50 pt-4">
              <p className="text-xs text-slate-400 font-medium mb-3">Daily Stop Times (UTC)</p>
              <p className="text-xs text-slate-500 mb-3">
                Cycles that fire after these hours are skipped and resume the following day. Set to <strong>0</strong> to disable the cutoff (run 24/7).
              </p>
              <div className="grid grid-cols-2 gap-4 max-w-sm">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Leads Stop Hour</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    value={globalForm.banker_leads_stop_hour}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_leads_stop_hour: Number(e.target.value) })}
                    className={inputClass}
                  />
                  <p className="text-xs text-slate-500 mt-0.5">Banker leads cycle</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">IPO Scan Stop Hour</label>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    step={1}
                    value={globalForm.ipo_scan_stop_hour}
                    onChange={(e) => setGlobalForm({ ...globalForm, ipo_scan_stop_hour: Number(e.target.value) })}
                    className={inputClass}
                  />
                  <p className="text-xs text-slate-500 mt-0.5">IPO detection cycle</p>
                </div>
              </div>
            </div>
            <div className="max-w-xs">
              <label className="text-xs text-slate-400 block mb-1">Dashboard Domain</label>
              <input
                type="text"
                value={globalForm.app_domain}
                onChange={(e) => setGlobalForm({ ...globalForm, app_domain: e.target.value })}
                placeholder="invest.marome-investments.co.za"
                className={inputClass}
              />
              <p className="text-xs text-slate-500 mt-0.5">
                Used in "View in Dashboard" buttons in Teams and Discord notification cards.
              </p>
            </div>
            {/* Monitor sidecar webhooks — ops/infrastructure alerts */}
            <div className="border-t border-slate-700/50 pt-4">
              <p className="text-xs text-slate-400 font-medium mb-3">Monitor Alert Webhooks (ops channel)</p>
              <div className="space-y-3">
                <div className="max-w-md">
                  <label className="text-xs text-slate-400 block mb-1">Teams Webhook URL</label>
                  <input
                    type="url"
                    value={globalForm.teams_monitor_webhook_url}
                    onChange={(e) => setGlobalForm({ ...globalForm, teams_monitor_webhook_url: e.target.value })}
                    placeholder="https://outlook.office.com/webhook/..."
                    className={inputClass}
                  />
                  <p className="text-xs text-slate-500 mt-0.5">
                    Infrastructure health alerts (service up/down transitions) sent to this Teams channel.
                  </p>
                </div>
                <div className="max-w-md">
                  <label className="text-xs text-slate-400 block mb-1">Discord Webhook URL</label>
                  <input
                    type="url"
                    value={globalForm.discord_monitor_webhook_url}
                    onChange={(e) => setGlobalForm({ ...globalForm, discord_monitor_webhook_url: e.target.value })}
                    placeholder="https://discord.com/api/webhooks/..."
                    className={inputClass}
                  />
                  <p className="text-xs text-slate-500 mt-0.5">
                    Same alerts to a Discord server channel. Leave blank to disable.
                  </p>
                </div>
              </div>
            </div>
            {/* Support ticket webhooks — separate channel from ops/monitor alerts */}
            <div className="border-t border-slate-700/50 pt-4">
              <p className="text-xs text-slate-400 font-medium mb-3">Support Ticket Webhooks</p>
              <div className="space-y-3">
                <div className="max-w-md">
                  <label className="text-xs text-slate-400 block mb-1">Teams Webhook URL</label>
                  <input
                    type="url"
                    value={globalForm.teams_support_webhook_url}
                    onChange={(e) => setGlobalForm({ ...globalForm, teams_support_webhook_url: e.target.value })}
                    placeholder="https://outlook.office.com/webhook/..."
                    className={inputClass}
                  />
                  <p className="text-xs text-slate-500 mt-0.5">
                    New ticket and user replies are posted to this Teams channel.
                  </p>
                </div>
                <div className="max-w-md">
                  <label className="text-xs text-slate-400 block mb-1">Discord Webhook URL</label>
                  <input
                    type="url"
                    value={globalForm.discord_support_webhook_url}
                    onChange={(e) => setGlobalForm({ ...globalForm, discord_support_webhook_url: e.target.value })}
                    placeholder="https://discord.com/api/webhooks/..."
                    className={inputClass}
                  />
                  <p className="text-xs text-slate-500 mt-0.5">
                    Same notifications to a Discord channel. Leave blank to disable.
                  </p>
                </div>
              </div>
            </div>
            {/* Banker LLM — primary + fallback */}
            <div className="border-t border-slate-700/50 pt-4 space-y-3">
              <p className="text-xs text-slate-400">
                Primary LLM for the Analyst and Banker engines. On 503/429 the primary retries 3× then falls back to the fallback provider.
                Models marked <span className="text-amber-400 font-medium">†</span> use <code className="text-xs bg-slate-700 px-1 rounded">max_completion_tokens</code> — handled automatically by the compatibility shim.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Primary Provider</label>
                  <select
                    value={globalForm.banker_provider}
                    onChange={(e) => {
                      const provider = e.target.value;
                      setGlobalForm({ ...globalForm, banker_provider: provider, banker_model: BANKER_MODEL_DEFAULTS[provider] ?? '' });
                    }}
                    className={inputClass}
                  >
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                    <option value="gemini">Google Gemini</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Primary Model</label>
                  <select
                    value={globalForm.banker_model}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_model: e.target.value })}
                    className={inputClass}
                  >
                    {globalForm.banker_provider === 'openai' && (<>
                      <optgroup label="GPT-4 family (max_tokens)">
                        <option value="gpt-4o">gpt-4o</option>
                        <option value="gpt-4o-mini">gpt-4o-mini</option>
                        <option value="gpt-4-turbo">gpt-4-turbo</option>
                        <option value="gpt-4.1">gpt-4.1</option>
                        <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                      </optgroup>
                      <optgroup label="GPT-5 / o-series † (max_completion_tokens — auto-patched)">
                        <option value="gpt-5-mini">gpt-5-mini †</option>
                        <option value="gpt-5">gpt-5 †</option>
                        <option value="o4-mini">o4-mini †</option>
                        <option value="o3">o3 †</option>
                        <option value="o3-mini">o3-mini †</option>
                        <option value="o1">o1 †</option>
                        <option value="o1-mini">o1-mini †</option>
                      </optgroup>
                    </>)}
                    {globalForm.banker_provider === 'anthropic' && (<>
                      <option value="claude-opus-4-6">claude-opus-4-6</option>
                      <option value="claude-sonnet-4-6">claude-sonnet-4-6</option>
                      <option value="claude-haiku-4-5-20251001">claude-haiku-4-5</option>
                    </>)}
                    {globalForm.banker_provider === 'gemini' && (<>
                      <option value="gemini-2.0-flash">gemini-2.0-flash</option>
                      <option value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</option>
                      <option value="gemini-1.5-pro">gemini-1.5-pro</option>
                      <option value="gemini-1.5-flash">gemini-1.5-flash</option>
                    </>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Fallback Provider</label>
                  <select
                    value={globalForm.banker_fallback_provider}
                    onChange={(e) => {
                      const provider = e.target.value;
                      setGlobalForm({ ...globalForm, banker_fallback_provider: provider, banker_fallback_model: BANKER_MODEL_DEFAULTS[provider] ?? '' });
                    }}
                    className={inputClass}
                  >
                    <option value="gemini">Google Gemini</option>
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Fallback Model</label>
                  <select
                    value={globalForm.banker_fallback_model}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_fallback_model: e.target.value })}
                    className={inputClass}
                  >
                    {globalForm.banker_fallback_provider === 'openai' && (<>
                      <optgroup label="GPT-4 family (max_tokens)">
                        <option value="gpt-4o">gpt-4o</option>
                        <option value="gpt-4o-mini">gpt-4o-mini</option>
                        <option value="gpt-4-turbo">gpt-4-turbo</option>
                        <option value="gpt-4.1">gpt-4.1</option>
                        <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                      </optgroup>
                      <optgroup label="GPT-5 / o-series † (max_completion_tokens — auto-patched)">
                        <option value="gpt-5-mini">gpt-5-mini †</option>
                        <option value="gpt-5">gpt-5 †</option>
                        <option value="o4-mini">o4-mini †</option>
                        <option value="o3">o3 †</option>
                        <option value="o3-mini">o3-mini †</option>
                        <option value="o1">o1 †</option>
                        <option value="o1-mini">o1-mini †</option>
                      </optgroup>
                    </>)}
                    {globalForm.banker_fallback_provider === 'anthropic' && (<>
                      <option value="claude-opus-4-6">claude-opus-4-6</option>
                      <option value="claude-sonnet-4-6">claude-sonnet-4-6</option>
                      <option value="claude-haiku-4-5-20251001">claude-haiku-4-5</option>
                    </>)}
                    {globalForm.banker_fallback_provider === 'gemini' && (<>
                      <option value="gemini-2.0-flash">gemini-2.0-flash</option>
                      <option value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</option>
                      <option value="gemini-1.5-pro">gemini-1.5-pro</option>
                      <option value="gemini-1.5-flash">gemini-1.5-flash</option>
                    </>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Cache Durations */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-300">Cache Durations</h3>
              <p className="text-xs text-slate-500 mt-1">
                Controls how long market data is cached in Redis. Shorter values mean fresher data but higher EODHD API quota consumption.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Real-time Quote (sec)</label>
                <input
                  type="number"
                  min={60}
                  step={60}
                  value={globalForm.cache_ttl_quote}
                  onChange={(e) => { setGlobalValidationError(null); setGlobalForm({ ...globalForm, cache_ttl_quote: Number(e.target.value) }); }}
                  className={inputClass}
                />
                <p className="text-xs text-slate-500 mt-0.5">Default: 900 (15 min). Min 60 sec.</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Historical OHLCV (sec)</label>
                <input
                  type="number"
                  min={60}
                  step={3600}
                  value={globalForm.cache_ttl_historical}
                  onChange={(e) => { setGlobalValidationError(null); setGlobalForm({ ...globalForm, cache_ttl_historical: Number(e.target.value) }); }}
                  className={inputClass}
                />
                <p className="text-xs text-slate-500 mt-0.5">Default: 86400 (24 hr). Min 60 sec.</p>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Market Heatmap (sec)</label>
                <input
                  type="number"
                  min={60}
                  step={60}
                  value={globalForm.cache_ttl_heatmap}
                  onChange={(e) => { setGlobalValidationError(null); setGlobalForm({ ...globalForm, cache_ttl_heatmap: Number(e.target.value) }); }}
                  className={inputClass}
                />
                <p className="text-xs text-slate-500 mt-0.5">Default: 900 (15 min). Min 60 sec.</p>
              </div>
            </div>
          </div>

          {/* Banker Business Rules */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-5">
            <h3 className="text-sm font-semibold text-slate-300">Banker Business Rules</h3>

            {/* Scoring Group */}
            <div className="space-y-3">
              <p className="text-xs text-slate-400 font-medium">Scoring</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Convergence Bonus (pts)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={globalForm.banker_convergence_bonus}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_convergence_bonus: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_convergence_bonus && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_convergence_bonus}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">Gate score bonus when both engines agree on the same symbol. Default: 10.</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Sector Alignment Bonus (pts)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={globalForm.banker_sector_bonus}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_sector_bonus: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_sector_bonus && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_sector_bonus}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">Gate score bonus when sector ETF trend aligns with direction. Default: 5.</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">High Confidence Floor (pts)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={globalForm.banker_high_confidence_floor}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_high_confidence_floor: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_high_confidence_floor && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_high_confidence_floor}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">Gate score that triggers HIGH priority (vs MEDIUM). Default: 75.</p>
                </div>
              </div>
            </div>

            {/* Position Risk Alerts Group */}
            <div className="border-t border-slate-700/50 pt-4 space-y-3">
              <p className="text-xs text-slate-400 font-medium">Position Risk Alerts</p>
              <p className="text-xs text-slate-500">P&L % drawdown that triggers an URGENT alert for conservative / moderate / aggressive risk profiles.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Warning % (conservative)</label>
                  <input
                    type="number"
                    min={0.1}
                    step={0.5}
                    value={globalForm.banker_risk_warn_pct}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_risk_warn_pct: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_risk_warn_pct && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_risk_warn_pct}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Alert % (moderate)</label>
                  <input
                    type="number"
                    min={0.1}
                    step={0.5}
                    value={globalForm.banker_risk_alert_pct}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_risk_alert_pct: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_risk_alert_pct && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_risk_alert_pct}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Critical % (aggressive)</label>
                  <input
                    type="number"
                    min={0.1}
                    step={0.5}
                    value={globalForm.banker_risk_critical_pct}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_risk_critical_pct: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_risk_critical_pct && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_risk_critical_pct}</p>
                  )}
                </div>
              </div>
            </div>

            {/* P&L Milestones Group */}
            <div className="border-t border-slate-700/50 pt-4 space-y-3">
              <p className="text-xs text-slate-400 font-medium">P&L Milestones</p>
              <p className="text-xs text-slate-500">P&L % gain/loss that triggers an ADVISORY milestone alert.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Minor Milestone % gain</label>
                  <input
                    type="number"
                    min={0.1}
                    step={0.5}
                    value={globalForm.banker_pnl_minor_milestone_pct}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_pnl_minor_milestone_pct: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_pnl_minor_milestone_pct && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_pnl_minor_milestone_pct}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">Default: 5%</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Major Milestone % gain</label>
                  <input
                    type="number"
                    min={0.1}
                    step={0.5}
                    value={globalForm.banker_pnl_major_milestone_pct}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_pnl_major_milestone_pct: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_pnl_major_milestone_pct && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_pnl_major_milestone_pct}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">Default: 10%</p>
                </div>
              </div>
            </div>

            {/* Discovery Group */}
            <div className="border-t border-slate-700/50 pt-4 space-y-3">
              <p className="text-xs text-slate-400 font-medium">Discovery</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs text-slate-400 block mb-1">ATR Proximity Multiplier</label>
                  <input
                    type="number"
                    min={0.1}
                    step={0.1}
                    value={globalForm.banker_atr_proximity_multiplier}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_atr_proximity_multiplier: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_atr_proximity_multiplier && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_atr_proximity_multiplier}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">S/R proximity threshold in ATR units for ADVISORY alerts. Default: 1.5.</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Universe Target (symbols)</label>
                  <input
                    type="number"
                    min={10}
                    max={200}
                    step={5}
                    value={globalForm.banker_universe_target}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_universe_target: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_universe_target && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_universe_target}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">Total symbol cap for opportunity discovery. Default: 60.</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Min Market Cap (USD)</label>
                  <input
                    type="number"
                    min={0}
                    step={1000000}
                    value={globalForm.banker_min_market_cap_usd}
                    onChange={(e) => setGlobalForm({ ...globalForm, banker_min_market_cap_usd: Number(e.target.value) })}
                    className={inputClass}
                  />
                  {bankerRulesErrors.banker_min_market_cap_usd && (
                    <p className="text-xs text-red-400 mt-0.5">{bankerRulesErrors.banker_min_market_cap_usd}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">Minimum market cap to include in discovery. Default: $100M. Set to 0 to disable filter.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Web Search Settings */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-300">Web Search</h3>
              <p className="text-xs text-slate-500 mt-1">
                Used by the banker cycle for news, sector trends, and event checks. Brave is recommended — it has its own quota separate from Gemini.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Primary Provider</label>
                <select
                  value={globalForm.web_search_provider}
                  onChange={(e) => setGlobalForm({ ...globalForm, web_search_provider: e.target.value })}
                  className={inputClass}
                >
                  <option value="exa">Exa Search (MCP)</option>
                  <option value="brave">Brave Search</option>
                  <option value="google">Google Custom Search</option>
                  <option value="bing">Bing Search</option>
                  <option value="openai">OpenAI (web_search_preview)</option>
                  <option value="gemini">Google Gemini (grounding)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Fallback Provider</label>
                <select
                  value={globalForm.web_search_fallback_provider}
                  onChange={(e) => setGlobalForm({ ...globalForm, web_search_fallback_provider: e.target.value })}
                  className={inputClass}
                >
                  <option value="">None (disabled)</option>
                  <option value="brave">Brave Search</option>
                  <option value="exa">Exa Search (MCP)</option>
                  <option value="openai">OpenAI (web_search_preview)</option>
                  <option value="gemini">Google Gemini (grounding)</option>
                  <option value="bing">Bing Search</option>
                </select>
                <p className="text-xs text-slate-500 mt-0.5">Used when primary returns 0 results</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-slate-400 block mb-1">Brave API Key</label>
                <input
                  type="password"
                  value={globalForm.web_search_api_key === 'configured' ? '' : globalForm.web_search_api_key}
                  onChange={(e) => setGlobalForm({ ...globalForm, web_search_api_key: e.target.value })}
                  placeholder={globalForm.web_search_api_key === 'configured' ? 'API key configured (leave blank to keep)' : 'Enter Brave API key'}
                  className={inputClass}
                />
                {globalForm.web_search_api_key === 'configured' && (
                  <p className="text-xs text-teal-400 mt-0.5">Brave API key is configured</p>
                )}
                {(globalForm.web_search_provider === 'gemini' || globalForm.web_search_fallback_provider === 'gemini') && (
                  <p className="text-xs text-slate-500 mt-0.5">Gemini uses the Gemini API key above — no separate key needed</p>
                )}
                {(globalForm.web_search_provider === 'openai' || globalForm.web_search_fallback_provider === 'openai') && (
                  <p className="text-xs text-slate-500 mt-0.5">OpenAI uses the OpenAI API key above — no separate key needed</p>
                )}
              </div>
              {(globalForm.web_search_provider === 'exa' || globalForm.web_search_fallback_provider === 'exa') && (
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-400 block mb-1">Exa API Key</label>
                  <input
                    type="password"
                    value={globalForm.exa_api_key === 'configured' ? '' : globalForm.exa_api_key}
                    onChange={(e) => setGlobalForm({ ...globalForm, exa_api_key: e.target.value })}
                    placeholder={globalForm.exa_api_key === 'configured' ? 'API key configured (leave blank to keep)' : 'Enter Exa API key'}
                    className={inputClass}
                  />
                  {globalForm.exa_api_key === 'configured' && (
                    <p className="text-xs text-teal-400 mt-0.5">Exa API key is configured</p>
                  )}
                  <p className="text-xs text-slate-500 mt-0.5">Exa hosted MCP — $3/1k searches vs Brave $5/1k</p>
                </div>
              )}
            </div>
          </div>

          {/* Video Transcript Settings */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <div>
              <div className="flex items-center gap-2">
                <Video size={16} className="text-purple-400" />
                <h3 className="text-sm font-semibold text-slate-300">Video Transcript</h3>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Configure how trading videos are processed for strategy extraction. Hybrid mode uses Whisper for audio + GPT for frames; Gemini mode sends the full video natively.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 block mb-1">Video Provider</label>
                <select
                  value={globalForm.video_provider}
                  onChange={(e) => setGlobalForm({ ...globalForm, video_provider: e.target.value })}
                  className={inputClass}
                >
                  <option value="hybrid">Hybrid (Whisper + GPT)</option>
                  <option value="gemini">Gemini (Native Video)</option>
                </select>
              </div>
              {globalForm.video_provider === 'hybrid' && (
                <>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Video Model (frame analysis)</label>
                    <select
                      value={globalForm.video_model}
                      onChange={(e) => setGlobalForm({ ...globalForm, video_model: e.target.value })}
                      className={inputClass}
                    >
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-5-mini">GPT-5 Mini</option>
                      <option value="gpt-4.1">GPT-4.1</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Whisper Model (audio transcription)</label>
                    <select
                      value={globalForm.whisper_model}
                      onChange={(e) => setGlobalForm({ ...globalForm, whisper_model: e.target.value })}
                      className={inputClass}
                    >
                      <option value="tiny">Tiny (fastest)</option>
                      <option value="base">Base (default)</option>
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large (most accurate)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 block mb-1">Frame Interval (seconds)</label>
                    <input
                      type="number"
                      min={1}
                      max={30}
                      value={globalForm.frame_interval}
                      onChange={(e) => setGlobalForm({ ...globalForm, frame_interval: Number(e.target.value) })}
                      className={inputClass}
                    />
                    <p className="text-xs text-slate-500 mt-0.5">How often to extract a frame for visual analysis</p>
                  </div>
                </>
              )}
              {globalForm.video_provider === 'gemini' && (
                <div>
                  <label className="text-xs text-slate-400 block mb-1">Gemini Video Model</label>
                  <select
                    value={globalForm.gemini_video_model}
                    onChange={(e) => setGlobalForm({ ...globalForm, gemini_video_model: e.target.value })}
                    className={inputClass}
                  >
                    <option value="gemini-3-flash-preview">Gemini 3 Flash (Fast)</option>
                    <option value="gemini-3-flash-thinking">Gemini 3 Flash Thinking</option>
                    <option value="gemini-3-pro-preview">Gemini 3 Pro</option>
                  </select>
                </div>
              )}
            </div>
            {/* Gemini API Key */}
            <div>
              <label className="text-xs text-slate-400 block mb-1">Gemini API Key</label>
              <input
                type="password"
                value={globalForm.gemini_api_key === 'configured' ? '' : globalForm.gemini_api_key}
                onChange={(e) => setGlobalForm({ ...globalForm, gemini_api_key: e.target.value })}
                placeholder={globalForm.gemini_api_key === 'configured' ? 'API key configured (leave blank to keep)' : 'Enter Google Gemini API key'}
                className={inputClass}
              />
              {globalForm.gemini_api_key === 'configured' && (
                <p className="text-xs text-teal-400 mt-0.5">API key is configured</p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Gemini Model (Expert Chat)</label>
              <select
                value={globalForm.gemini_model}
                onChange={(e) => setGlobalForm({ ...globalForm, gemini_model: e.target.value })}
                className={inputClass}
              >
                <option value="gemini-3-flash-preview">Gemini 3 Flash (Fast)</option>
                <option value="gemini-3-flash-thinking">Gemini 3 Flash Thinking</option>
                <option value="gemini-3-pro-preview">Gemini 3 Pro</option>
              </select>
              <p className="text-xs text-slate-500 mt-0.5">Used when Expert Chat provider is set to Gemini</p>
            </div>
          </div>

          {/* Mobile Push Notifications */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 text-orange-400">📱</div>
              <h3 className="text-sm font-semibold text-slate-300">Mobile Push Notifications</h3>
            </div>
            <p className="text-xs text-slate-500">
              Push notifications are delivered via the{' '}
              <span className="text-slate-300">Expo Push Notification Service</span> (exp.host),
              which handles both Android (FCM) and iOS (APNs) without server-side credentials.
              The access token is optional — leave blank for standard rate limits.
            </p>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Expo Access Token (optional)</label>
              <input
                type="password"
                value={globalForm.expo_access_token === 'configured' ? '' : globalForm.expo_access_token}
                onChange={(e) => setGlobalForm({ ...globalForm, expo_access_token: e.target.value })}
                placeholder={globalForm.expo_access_token === 'configured' ? 'Configured (leave blank to keep)' : 'Leave blank for standard rate limits'}
                className={inputClass}
              />
              {globalForm.expo_access_token === 'configured' && (
                <p className="text-xs text-teal-400 mt-0.5">Expo access token configured</p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Push Worker Concurrency (1–1000)</label>
              <input
                type="number"
                min={1}
                max={1000}
                value={globalForm.push_worker_concurrency}
                onChange={(e) => setGlobalForm({ ...globalForm, push_worker_concurrency: Math.max(1, Math.min(1000, parseInt(e.target.value) || 200)) })}
                className={inputClass}
              />
              <p className="text-xs text-slate-500 mt-0.5">Max concurrent outbound Expo push calls during a broadcast. Default: 200.</p>
            </div>
          </div>

          {/* Skill Map (read-only) */}
          {globalConfig?.trading_skill_map && Object.keys(globalConfig.trading_skill_map).length > 0 && (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-3">
              <h3 className="text-sm font-semibold text-slate-300">Skill Mapping</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(globalConfig.trading_skill_map).map(([symbol, dir]) => (
                  <div key={symbol} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2 text-sm">
                    <span className="text-white font-medium">{symbol}</span>
                    <span className="text-slate-400">{dir}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">Edit TRADING_SKILL_MAP in .env to modify symbol-to-skill mappings.</p>
            </div>
          )}
        </div>
      )}

      {/* ══════ Section 5: Tools (always visible) ══════ */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-white">Tools</h2>

        {/* Quick Links */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-3">
          <h3 className="text-sm font-semibold text-slate-300">Quick Links</h3>
          <button
            onClick={() => navigate('/trading/skills')}
            className="flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300 transition-colors"
          >
            <ExternalLink size={14} />
            Open Skill Editor
          </button>
          <button
            onClick={() => navigate('/trading/test-provider')}
            className="flex items-center gap-2 text-sm text-teal-400 hover:text-teal-300 transition-colors"
          >
            <Zap size={14} />
            Test Provider Connectivity
          </button>
        </div>

        {/* Manual Review Triggers */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-300">Manual Review Triggers</h3>
          <p className="text-xs text-slate-500">Trigger a trade review cycle manually. Reviews run automatically on schedule.</p>
          <div className="flex flex-wrap gap-3">
            {(['daily', 'weekly', 'monthly'] as const).map((period) => (
              <button
                key={period}
                onClick={() => handleReview(period)}
                disabled={triggerReview.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                <Play size={14} />
                {period.charAt(0).toUpperCase() + period.slice(1)} Review
              </button>
            ))}
          </div>
          {reviewStatus && (
            <p className="text-xs text-teal-400">{reviewStatus}</p>
          )}
        </div>

        {/* Maintenance */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 space-y-4">
          <h3 className="text-sm font-semibold text-slate-300">Maintenance</h3>
          <div>
            <p className="text-xs text-slate-500 mb-3">
              Repair trade-to-analysis links broken by MT5 comment truncation. Matches truncated IDs to full analysis records.
            </p>
            <button
              onClick={() => {
                setRepairStatus('Repairing...');
                repairLinks.mutate(undefined, {
                  onSuccess: (data) => setRepairStatus(`Done: ${data.repaired} repaired, ${data.already_ok} already linked, ${data.unresolved} unresolved`),
                  onError: (e) => setRepairStatus(`Failed: ${e.message}`),
                });
              }}
              disabled={repairLinks.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors disabled:opacity-50"
            >
              <Wrench size={14} />
              Repair Trade Links
            </button>
            {repairStatus && (
              <p className={`text-xs mt-2 ${repairStatus.startsWith('Failed') ? 'text-red-400' : 'text-teal-400'}`}>{repairStatus}</p>
            )}
          </div>

          {isAdmin && (
            <div>
              <p className="text-xs text-slate-500 mb-3">
                Auto-populate EA parameters for strategies that were added before v10. Dry run previews what would be processed without writing to the database.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => {
                    setBackfillStatus('Running dry run...');
                    backfill.mutate(true, {
                      onSuccess: (data) => setBackfillStatus(
                        `Dry run: ${data.would_process} would be processed, ${data.skipped} already have parameters`
                      ),
                      onError: (e) => setBackfillStatus(`Failed: ${e.message}`),
                    });
                  }}
                  disabled={backfill.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  <RotateCcw size={14} />
                  Dry Run
                </button>
                <button
                  onClick={() => {
                    setBackfillStatus('Running backfill...');
                    backfill.mutate(false, {
                      onSuccess: (data) => setBackfillStatus(
                        `Done: ${data.succeeded} succeeded, ${data.failed} failed, ${data.skipped} skipped`
                      ),
                      onError: (e) => setBackfillStatus(`Failed: ${e.message}`),
                    });
                  }}
                  disabled={backfill.isPending}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-700 hover:bg-teal-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                >
                  <Zap size={14} />
                  Backfill Strategy Parameters
                </button>
              </div>
              {backfillStatus && (
                <p className={`text-xs mt-2 ${backfillStatus.startsWith('Failed') ? 'text-red-400' : 'text-teal-400'}`}>{backfillStatus}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradingSettings;
